import { useRef, useState } from 'react'
import { Camera, Loader2, CheckCircle } from 'lucide-react'
import { uploadImage } from '@/integrations/supabase/client'

interface Props {
  slotId: string
  src: string
  alt: string
  className?: string
  imgClassName?: string
  editMode: boolean
  onNewImage: (slotId: string, publicUrl: string) => void
  onClick?: () => void
  forcePortrait?: boolean
}

export function EditableImage({
  slotId, src, alt, className = '', imgClassName = '',
  editMode, onNewImage, onClick, forcePortrait = false,
}: Props) {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  function openFilePicker(e: React.MouseEvent) {
    e.stopPropagation()
    setErrorMsg(null); setSuccess(false)
    inputRef.current?.click()
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setLoading(true); setErrorMsg(null)

    const reader = new FileReader()
    reader.onload = (evt) => {
      const dataUrl = evt.target?.result as string
      const img = new Image()
      img.onload = async () => {
        try {
          const MAX = 1200
          let { width, height } = img
          if (width > MAX || height > MAX) {
            if (width > height) { height = Math.round(height * MAX / width); width = MAX }
            else { width = Math.round(width * MAX / height); height = MAX }
          }
          const canvas = document.createElement('canvas')
          canvas.width = width; canvas.height = height
          canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
          const blob = await new Promise<Blob>((res, rej) =>
            canvas.toBlob(b => b ? res(b) : rej(new Error('toBlob failed')), 'image/jpeg', 0.85))
          // Upload sur Supabase Storage → URL permanente et publique
          const publicUrl = await uploadImage(slotId, blob)
          onNewImage(slotId, publicUrl)
          setSuccess(true)
          setTimeout(() => setSuccess(false), 3000)
        } catch (err) {
          console.error(err)
          setErrorMsg('Upload échoué. Vérifiez votre connexion.')
        } finally {
          setLoading(false)
          if (inputRef.current) inputRef.current.value = ''
        }
      }
      img.src = dataUrl
    }
    reader.readAsDataURL(file)
  }

  const wrapperCls = forcePortrait
    ? `relative aspect-[3/4] overflow-hidden ${className}`
    : `relative overflow-hidden ${className}`
  const imgCls = forcePortrait
    ? `absolute inset-0 w-full h-full object-cover ${imgClassName}`
    : `w-full h-full object-cover ${imgClassName}`

  return (
    <div className={wrapperCls}>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      <img src={src} alt={alt} className={imgCls}
        onClick={!editMode ? onClick : undefined}
        style={!editMode && onClick ? { cursor: 'pointer' } : undefined} />
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-20">
          <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
          <span className="text-white text-sm font-medium">Upload en cours…</span>
        </div>
      )}
      {success && !loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 z-20 pointer-events-none">
          <CheckCircle className="w-10 h-10 text-green-400 mb-2" />
          <span className="text-white text-sm font-medium">Photo enregistrée ✓</span>
        </div>
      )}
      {errorMsg && !loading && (
        <div className="absolute bottom-2 left-2 right-2 bg-red-900/90 text-white text-xs rounded-lg p-2 z-20">{errorMsg}</div>
      )}
      {editMode && !loading && !success && (
        <div onClick={openFilePicker}
          className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 opacity-0 hover:opacity-100 transition-opacity z-10 cursor-pointer select-none">
          <Camera className="w-8 h-8 text-white mb-2" />
          <span className="text-white text-sm font-semibold">Changer la photo</span>
          <span className="text-white/70 text-xs mt-1">Depuis votre appareil ou Google Photos</span>
        </div>
      )}
    </div>
  )
}
